import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import { playArrivedChime } from "../../../../helpers/arrivedFeedback";
import {
  confirmClinicalGateContinue,
  extractClinicalGateAlertsFromError,
  isClinicalGateAckRequired,
} from "../../../../helpers/promptClinicalGate";
import {
  callEntry,
  callNext,
  dismissEntry,
  formatWaitDuration,
  listWaitingRoom,
  moveEntry,
  startService,
  WaitingRoomEntry,
} from "../../../../services/waitingRoomService";

interface WaitingRoomPanelProps {
  unitId: number | null;
  date: string;
  canManage: boolean;
  onOpenTv: () => void;
  refreshKey?: number;
  onWaitingCountChange?: (count: number) => void;
}

const statusColor = (
  status: WaitingRoomEntry["waiting_room_status"]
): string => {
  if (status === "called") {
    return "warning";
  }
  if (status === "in_service") {
    return "success";
  }
  return "info";
};

const WaitingRoomPanel = ({
  unitId,
  date,
  canManage,
  onOpenTv,
  refreshKey = 0,
  onWaitingCountChange,
}: WaitingRoomPanelProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | "next" | null>(null);
  const [entries, setEntries] = useState<WaitingRoomEntry[]>([]);
  const [tick, setTick] = useState(0);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!unitId) {
        setEntries([]);
        onWaitingCountChange?.(0);
        return;
      }

      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const response = await listWaitingRoom({ unit_id: unitId, date });
        const next = Array.isArray(response.data) ? response.data : [];
        setEntries(next);
        const waiting = next.filter(
          (entry) => entry.waiting_room_status === "waiting"
        ).length;
        onWaitingCountChange?.(
          typeof response.meta?.waiting_count === "number"
            ? response.meta.waiting_count
            : waiting
        );
      } catch {
        if (!opts?.silent) {
          toast.error(t("studio.agenda.waitingRoom.toast.loadError"));
        }
        setEntries([]);
        onWaitingCountChange?.(0);
      } finally {
        if (!opts?.silent) {
          setLoading(false);
        }
      }
    },
    [date, onWaitingCountChange, t, unitId]
  );

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    const tickTimer = window.setInterval(
      () => setTick((value) => value + 1),
      30000
    );
    return () => window.clearInterval(tickTimer);
  }, []);

  useEffect(() => {
    if (!unitId) {
      return undefined;
    }
    const pollTimer = window.setInterval(() => {
      void load({ silent: true });
    }, 15000);
    return () => window.clearInterval(pollTimer);
  }, [load, unitId]);

  const waitingEntries = useMemo(
    () => entries.filter((entry) => entry.waiting_room_status === "waiting"),
    [entries]
  );
  const waitingCount = waitingEntries.length;

  const handleCallNext = async () => {
    if (!unitId || !canManage) {
      return;
    }

    setBusyId("next");
    try {
      const response = await callNext({ unit_id: unitId, date });
      playArrivedChime();
      toast.success(
        t("studio.agenda.waitingRoom.toast.called", {
          name:
            response.data?.client?.first_name ||
            response.data?.client?.name ||
            "",
        })
      );
      await load({ silent: true });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ||
        t("studio.agenda.waitingRoom.toast.callError");
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  };

  const runAction = async (
    appointmentId: number,
    action: "call" | "start" | "dismiss" | "up" | "down"
  ) => {
    if (!canManage) {
      return;
    }

    setBusyId(appointmentId);
    try {
      if (action === "call") {
        const response = await callEntry(appointmentId);
        playArrivedChime();
        toast.success(
          t("studio.agenda.waitingRoom.toast.called", {
            name:
              response.data?.client?.first_name ||
              response.data?.client?.name ||
              "",
          })
        );
      } else if (action === "start") {
        try {
          await startService(appointmentId);
        } catch (startError: unknown) {
          if (!isClinicalGateAckRequired(startError)) {
            throw startError;
          }
          const mayProceed = await confirmClinicalGateContinue({
            t,
            alerts: extractClinicalGateAlertsFromError(startError),
          });
          if (!mayProceed) {
            return;
          }
          await startService(appointmentId, { acknowledge_clinical_gate: true });
        }
        toast.success(t("studio.agenda.waitingRoom.toast.started"));
      } else if (action === "dismiss") {
        await dismissEntry(appointmentId);
        toast.success(t("studio.agenda.waitingRoom.toast.dismissed"));
      } else {
        await moveEntry(appointmentId, action);
        toast.success(t("studio.agenda.waitingRoom.toast.reordered"));
      }
      await load({ silent: true });
    } catch (error: unknown) {
      const message =
        (error as { data?: string; response?: { data?: { error?: { message?: string } } } })
          ?.data ||
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ||
        t("studio.agenda.waitingRoom.toast.actionError");
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  };

  if (!unitId) {
    return (
      <Alert color="info" className="mb-0">
        {t("studio.agenda.waitingRoom.selectUnit")}
      </Alert>
    );
  }

  return (
    <div className="waiting-room-panel">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <h5 className="mb-0">{t("studio.agenda.waitingRoom.title")}</h5>
          <Badge color="primary" pill>
            {t("studio.agenda.waitingRoom.waitingCount", {
              count: waitingCount,
            })}
          </Badge>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button color="light" size="sm" onClick={() => void load()}>
            <i className="ri-refresh-line align-bottom me-1" />
            {t("studio.agenda.waitingRoom.actions.refresh")}
          </Button>
          <Button color="secondary" outline size="sm" onClick={onOpenTv}>
            <i className="ri-tv-line align-bottom me-1" />
            {t("studio.agenda.waitingRoom.actions.tvMode")}
          </Button>
          {canManage ? (
            <Button
              color="primary"
              size="sm"
              disabled={busyId === "next" || waitingCount === 0}
              onClick={() => void handleCallNext()}
            >
              {busyId === "next" ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="ri-volume-up-line align-bottom me-1" />
              )}
              {t("studio.agenda.waitingRoom.actions.callNext")}
            </Button>
          ) : null}
        </div>
      </div>

      {loading && entries.length === 0 ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : entries.length === 0 ? (
        <Alert color="light" className="border text-center mb-0 py-4">
          <i className="ri-user-smile-line fs-2 d-block mb-2 text-muted" />
          <div className="fw-medium">
            {t("studio.agenda.waitingRoom.empty.title")}
          </div>
          <div className="text-muted small">
            {t("studio.agenda.waitingRoom.empty.hint")}
          </div>
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table className="align-middle table-nowrap mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>{t("studio.agenda.waitingRoom.columns.client")}</th>
                <th>{t("studio.agenda.waitingRoom.columns.professional")}</th>
                <th>{t("studio.agenda.waitingRoom.columns.wait")}</th>
                <th>{t("studio.agenda.waitingRoom.columns.status")}</th>
                {canManage ? (
                  <th className="text-end">
                    {t("studio.agenda.waitingRoom.columns.actions")}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                void tick;
                const arrivedMs = entry.client_arrived_at
                  ? new Date(entry.client_arrived_at).getTime()
                  : NaN;
                const liveSeconds = Number.isFinite(arrivedMs)
                  ? Math.max(0, Math.floor((Date.now() - arrivedMs) / 1000))
                  : entry.wait_seconds;
                const waitingIndex = waitingEntries.findIndex(
                  (item) => item.id === entry.id
                );
                const canMoveUp =
                  entry.waiting_room_status === "waiting" && waitingIndex > 0;
                const canMoveDown =
                  entry.waiting_room_status === "waiting" &&
                  waitingIndex >= 0 &&
                  waitingIndex < waitingEntries.length - 1;

                return (
                  <tr key={entry.id}>
                    <td className="text-muted">{entry.position ?? "—"}</td>
                    <td>
                      <div className="fw-medium">
                        {entry.client?.name || entry.client?.first_name || "—"}
                      </div>
                      <div className="text-muted small">
                        {entry.service?.name || "—"}
                      </div>
                    </td>
                    <td>{entry.professional?.name || "—"}</td>
                    <td>
                      <span className="badge bg-light text-body border">
                        {formatWaitDuration(liveSeconds)}
                      </span>
                    </td>
                    <td>
                      <Badge color={statusColor(entry.waiting_room_status)} pill>
                        {t(
                          `studio.agenda.waitingRoom.status.${entry.waiting_room_status || "waiting"}`
                        )}
                      </Badge>
                    </td>
                    {canManage ? (
                      <td className="text-end">
                        <div className="d-inline-flex flex-wrap gap-1 justify-content-end align-items-center">
                          {entry.waiting_room_status === "waiting" ? (
                            <ButtonGroup size="sm">
                              <Button
                                color="light"
                                disabled={busyId === entry.id || !canMoveUp}
                                title={t(
                                  "studio.agenda.waitingRoom.actions.moveUp"
                                )}
                                aria-label={t(
                                  "studio.agenda.waitingRoom.actions.moveUp"
                                )}
                                onClick={() => void runAction(entry.id, "up")}
                              >
                                <i className="ri-arrow-up-line" />
                              </Button>
                              <Button
                                color="light"
                                disabled={busyId === entry.id || !canMoveDown}
                                title={t(
                                  "studio.agenda.waitingRoom.actions.moveDown"
                                )}
                                aria-label={t(
                                  "studio.agenda.waitingRoom.actions.moveDown"
                                )}
                                onClick={() => void runAction(entry.id, "down")}
                              >
                                <i className="ri-arrow-down-line" />
                              </Button>
                            </ButtonGroup>
                          ) : null}
                          {entry.waiting_room_status === "waiting" ? (
                            <Button
                              color="warning"
                              outline
                              size="sm"
                              disabled={busyId === entry.id}
                              onClick={() => void runAction(entry.id, "call")}
                            >
                              {t("studio.agenda.waitingRoom.actions.call")}
                            </Button>
                          ) : null}
                          {entry.waiting_room_status !== "in_service" ? (
                            <Button
                              color="success"
                              outline
                              size="sm"
                              disabled={busyId === entry.id}
                              onClick={() => void runAction(entry.id, "start")}
                            >
                              {t("studio.agenda.waitingRoom.actions.start")}
                            </Button>
                          ) : null}
                          <Button
                            color="light"
                            size="sm"
                            disabled={busyId === entry.id}
                            onClick={() => void runAction(entry.id, "dismiss")}
                          >
                            {t("studio.agenda.waitingRoom.actions.dismiss")}
                          </Button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default WaitingRoomPanel;
